import {useLocation} from '@reach/router';
import {useI18next} from 'gatsby-plugin-react-i18next';
import indexPreview from 'images/preview.png';
import React, {PropsWithRef, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {getHreflangLinks, HreflangAlternate, localizePath} from 'utils/hreflang';
import {initConnectChatWidget, updateConnectChatContext} from 'utils/connect-chat';

type Props = PropsWithRef<{
  title?: string;
  description?: string;
  suggestion?: string;
  preview?: string;
  canonical?: string;
  bodyAttributes?: Record<string, any>;
  schema?: any;
  scripts?: any;
  path?: string;
  noIndex?: boolean;
  // Overrides the hreflang alternates (e.g. Prismic docs, where only some translations exist).
  alternates?: HreflangAlternate[];
}>;

export const baseStructuredDataScriptFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  image: 'https://assets.monei.com/images/logo-square.png',
  sameAs: [
    'https://es.linkedin.com/company/monei-digital-payments',
    'https://www.instagram.com/moneipayments/',
    'https://monei.com/blog/'
  ]
};

export const SEO: React.FC<Props> = (props) => {
  const location = useLocation();
  const {t, language, languages, defaultLanguage, originalPath, siteUrl = ''} = useI18next();
  const title = props.title || t('🚀 Best Payment Platform for Business Growth');
  const description =
    props.description ||
    t(
      'Use MONEI’s plugins and APIs to accept more payment methods and increase sales online and in-person — all from a single payment platform.'
    );
  const preview = props.preview || indexPreview;
  const siteTitle = 'MONEI' + (location.pathname.includes('/monei-pay') ? ' Pay' : '');
  // Every page under /tools/ (in any language) is noindex, even without the noIndex prop.
  const noIndex = props.noIndex || /^\/(?:[a-z]{2}\/)?tools\//.test(location.pathname);

  let structuredDataScript;

  const replacesDefaultSchema =
    props.bodyAttributes?.class === 'blog' ||
    (props.bodyAttributes?.class === 'author' && Boolean(props.schema));

  if (replacesDefaultSchema) {
    structuredDataScript = props.schema;
  } else if (props.schema) {
    const isFAQPage = props.schema['@type'] === 'FAQPage';
    const baseProps = isFAQPage ? baseStructuredDataScriptFAQ : undefined;

    structuredDataScript = {
      ...baseProps,
      name: title,
      url:
        props.canonical ||
        'https://monei.com' + (props.path ? '/' + props.path : location.pathname),
      ...props.schema
    };
  }

  const hreflangLinks = getHreflangLinks({
    siteUrl,
    defaultLanguage,
    languages,
    originalPath,
    alternates: props.alternates
  });

  useEffect(() => {
    initConnectChatWidget(language);
    updateConnectChatContext(language);
  }, [language]);

  return (
    <Helmet defer={false} bodyAttributes={props.bodyAttributes}>
      <html lang={language} />
      <link
        rel="canonical"
        href={siteUrl + localizePath(language, defaultLanguage, originalPath)}
      />
      {hreflangLinks.map(({hrefLang, href}) => (
        <link key={hrefLang} rel="alternate" href={href} hrefLang={hrefLang} />
      ))}
      <meta charSet="utf-8" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="shortcut icon" href="https://assets.monei.com/images/icon.png" />
      {/* Facebook */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={preview} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={language} />
      <meta property="og:image:alt" content={description} />
      <meta
        name="robots"
        content={noIndex ? 'noindex,nofollow' : 'max-image-preview:large,max-snippet:-1'}
      />
      {/* Twitter */}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={preview} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image:alt" content={description} />

      {structuredDataScript && (
        <script type="application/ld+json">{JSON.stringify(structuredDataScript)}</script>
      )}
      {props.scripts && <script type="application/ld+json">{JSON.stringify(props.scripts)}</script>}
    </Helmet>
  );
};
